import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { IFormProfile } from 'legacy/lut/classes/models/IFormProfile'
import { IFormProfileExt } from 'legacy/lut/classes/models/IFormProfileExt'
import OnboardingStoreService from 'legacy/lut/classes/services/OnboardingStoreService'
import { DraftContactDialog } from 'legacy/lut/components/widgets/public/contactEditDialog/saveAsDraftContact/DraftContactDialog'
import { useState } from 'react'
import { useSelector } from 'react-redux'

import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { guid } from 'core/classes/helpers/GuidHelper'
import { VisualAudience } from 'core/components/shared/ui/VisualAudience'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { WidgetContactEditDialog } from './contactEditDialog/ContactEditDialog'

import styles from './AddContact.module.scss'

interface IProps {
  PartnerID: string
  cardView?: boolean
  isOnboarding?: boolean
  appSource?: string
  disabled?: boolean
  onApply?: () => void
  setContactSaved?: (contact: IFormProfile | IFormProfileExt | null) => void
}

interface IState {
  opened: boolean
  item: IFormProfile | null
}

export const WidgetAddContact = (props: IProps) => {
  const [state, setState] = useState<IState>({
    opened: false,
    item: null,
  })
  const roles = useSelector(selectUser).roleIds

  return (
    <>
      <ButtonAddNew props={props} setState={setState} />
      {state.opened &&
        state.item &&
        (props.isOnboarding ? (
          <DraftContactDialog
            draft={state.item}
            appSource={props.appSource}
            setContactSaved={props.setContactSaved}
            onApply={() => {
              props.onApply && props.onApply()
            }}
          />
        ) : (
          <VisualAudience<any>
            read={['Read Only']}
            write={roles.filter((el: string) => el !== 'Read Only')}
            component={WidgetContactEditDialog}
            appSource={props.appSource}
            dialogEntryId={Number(state.opened)}
            entryIdItem={state.item}
            isOnboarding={props.isOnboarding}
            onClose={() => {
              props.onApply && props.onApply()
              setState({ opened: false, item: null })
            }}
          />
        ))}
    </>
  )
}

const ButtonAddNew = ({
  props,
  setState,
}: {
  props: IProps
  setState: ({ item, opened }: IState) => void
}) => {
  const [wip, setWip] = useState(false)

  const onAddNewClick = async () => {
    if (!wip) {
      setWip(true)
      try {
        const item = await generateEmptyItem(props)
        setState({ item, opened: true })
      } catch (e) {
        Logger.error(e)
      } finally {
        setWip(false)
      }
    }
  }

  const label = () => {
    return wip ? (
      <Loader className={styles.loader} dimension={{ height: 22, width: 22 }} />
    ) : (
      'Add new'
    )
  }

  return !props.cardView ? (
    <Button
      margin={'sm'}
      variant={'contained'}
      color={'primary'}
      disabled={wip || props.disabled}
      onClick={onAddNewClick}
    >
      {label()}
    </Button>
  ) : (
    <div className={styles.card} onClick={onAddNewClick} tabIndex={0}>
      <span className={styles.card__text}>{label()}</span>
    </div>
  )
}

const generateEmptyItem = async (props: { PartnerID: string }): Promise<IFormProfile> => {
  const newItem = { ...props } as IFormProfile

  const partner = await OnboardingStoreService.getPartnerOrPartnerChangeRequest(props.PartnerID)

  newItem.PartnerID = props.PartnerID
  newItem.ParentPartnerID = partner ? partner.ParentPartnerID : props.PartnerID
  newItem.ContactID = `new-${guid()}`
  newItem.Country = partner ? partner.CountryExt || partner.Country : ''
  newItem.Market = partner?.Market || null

  await setNewContactCountry(newItem, partner)

  return newItem
}

const setNewContactCountry = async (newItem: IFormProfile, partner: any) => {
  if (partner && partner.Country) {
    const crudCountry = CrudStore.get(CrudStoreItem.CountriesTable, 'New Contact Country')
    const country = await crudCountry.getById({ id: partner.Country, keyField: 'en' })
    if (country) {
      newItem.CountryOfBusiness = country.CC
    }
  }
}
